import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { SystemCodesDetails } from "../../../constants/common";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import DeleteDetailCodeModal from "../../modal/DeleteDetailCodeModal";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface SystemCodesTableProps {
  allDetailCodes: never[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  setSelectedCodeId: Dispatch<SetStateAction<number>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const SystemCodesTable = ({
  allDetailCodes,
  pages,
  page,
  setPage,
  search,
  setSearch,
  setSelectedCodeId,
  count,
  pageSize,
  setPageSize,
}: SystemCodesTableProps) => {
  const { t } = useTranslation();

  const [isDeleteDetailCodeOpen, setIsDeleteDetailCodeOpen] = useState(false);
  const [selectedCodeDetails, setSelectedCodeDetails] = useState({
    masterCode: 0,
    detailCode: 0,
    isDeletable: 0,
  });

  const handleDeleteDetailCode = useCallback(
    async (masterCode: number, detailCode: number, deletable: number) => {
      if (deletable === 1) {
        toast.error("This code cannot be deleted");
        setIsDeleteDetailCodeOpen(false);
        return;
      }

      setIsDeleteDetailCodeOpen(true);
      setSelectedCodeDetails({
        masterCode,
        detailCode,
        isDeletable: deletable,
      });
    },
    []
  );

  const systemCodesColumns: ColumnDef<SystemCodesDetails>[] = useMemo(
    () => [
      {
        accessorKey: "n_DetailedCode",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("code")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() => setSelectedCodeId(row.original.n_DetailedCode)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_DetailedCode}
            </span>
          );
        },
      },
      {
        accessorKey: "s_Desc_E",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("descriptionEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Desc_E}</span>;
        },
      },
      {
        accessorKey: "s_Desc_A",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("descriptionAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Desc_A}</span>;
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleDeleteDetailCode(
                  row.original.n_MasterCode,
                  row.original.n_DetailedCode,
                  row.original.n_IsDefault
                )
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteDetailCode, setSelectedCodeId, t]
  );

  return (
    <>
      <TableWrapper
        data={allDetailCodes}
        columns={systemCodesColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isColumnChooser={false}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isDarkModeStyles={false}
      />

      <DeleteDetailCodeModal
        codeDetails={selectedCodeDetails}
        isOpen={isDeleteDetailCodeOpen}
        onOpenChange={() => setIsDeleteDetailCodeOpen(false)}
      />
    </>
  );
};

export default React.memo(SystemCodesTable);
